import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Calendar, Clock, Video, ArrowLeft, Loader2, ExternalLink, CheckCircle2,
  Target, ListChecks, FileText, ChevronDown, X,
} from 'lucide-react';
import PortalLayout from './PortalLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const muted = { color: 'var(--p-muted)' };

export default function ClientSessionDetail() {
  const { sessionId } = useParams();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showRoom, setShowRoom] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/portal/sessions/${sessionId}`, { withCredentials: true })
      .then((res) => setSession(res.data))
      .catch((err) => setError(err.response?.status === 404 ? 'Session not found.' : 'Could not load this session.'))
      .finally(() => setLoading(false));
  }, [sessionId]);

  if (loading) {
    return (
      <PortalLayout active="sessions">
        <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#1D4ED8]" size={28} /></div>
      </PortalLayout>
    );
  }

  if (error || !session) {
    return (
      <PortalLayout active="sessions">
        <div className="p-card rounded-2xl p-10 text-center" data-testid="session-detail-error">
          <p className="mb-4" style={muted}>{error || 'Session not found.'}</p>
          <Link to="/portal/sessions" className="inline-flex items-center gap-2 text-[#1D4ED8] font-medium"><ArrowLeft size={16} /> Back to sessions</Link>
        </div>
      </PortalLayout>
    );
  }

  const isCompleted = session.status === 'completed';
  const takeaways = session.ai_key_takeaways || [];
  const actionPlan = session.ai_action_plan || [];
  const goals = session.ai_goals || [];
  const hasRecap = session.ai_summary || takeaways.length || actionPlan.length;
  const jitsiUrl = `https://meet.jit.si/${session.room_name || `DCAEnterprises-${sessionId}`}`;

  return (
    <PortalLayout active="sessions">
      <Link to="/portal/sessions" className="inline-flex items-center gap-2 text-sm mb-6" style={muted} data-testid="back-to-sessions">
        <ArrowLeft size={15} /> All sessions
      </Link>

      {/* Header */}
      <div className="p-card rounded-2xl p-6 mb-6" data-testid="session-detail-header">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl tracking-tight font-light" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="session-detail-title">{session.title}</h1>
            <div className="flex flex-wrap items-center gap-4 mt-3 text-sm" style={muted}>
              <span className="flex items-center gap-1.5"><Calendar size={14} className="text-[#1D4ED8]" />{session.session_date || 'TBD'}</span>
              {session.session_time && <span className="flex items-center gap-1.5"><Clock size={14} className="text-[#1D4ED8]" />{session.session_time}</span>}
              <span className="flex items-center gap-1.5"><Video size={14} className="text-[#1D4ED8]" />{(session.session_type || 'one on one').replace(/_/g, ' ')}</span>
              <span>{session.duration || '60 min'}</span>
            </div>
          </div>
          <span
            className="text-xs px-3 py-1 rounded-full font-medium whitespace-nowrap"
            style={isCompleted ? { background: 'rgba(16,185,129,0.12)', color: '#059669' } : { background: 'rgba(29,78,216,0.12)', color: '#1D4ED8' }}
            data-testid="session-detail-status"
          >
            {isCompleted ? 'Completed' : 'Upcoming'}
          </span>
        </div>

        {/* Join actions (upcoming only) */}
        {!isCompleted && (
          <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="session-join-actions">
            {session.zoom_url ? (
              <a href={session.zoom_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#1D4ED8] hover:bg-[#2563EB] text-white px-5 py-3 rounded-lg font-medium transition-colors" data-testid="join-zoom-btn">
                <Video size={17} /> Join via Zoom <ExternalLink size={14} />
              </a>
            ) : (
              <button onClick={() => setShowRoom(true)}
                className="inline-flex items-center gap-2 bg-[#1D4ED8] hover:bg-[#2563EB] text-white px-5 py-3 rounded-lg font-medium transition-colors" data-testid="join-native-btn">
                <Video size={17} /> Join session room
              </button>
            )}
            <span className="text-sm" style={muted}>
              {session.zoom_url ? 'Your coach will host on Zoom.' : 'No download needed. Opens right here in your portal.'}
            </span>
          </div>
        )}
      </div>

      {/* Native room */}
      {showRoom && (
        <div className="p-card rounded-2xl p-3 mb-6" data-testid="native-room">
          <div className="flex items-center justify-between px-2 py-1 mb-2">
            <span className="text-sm font-medium flex items-center gap-2"><Video size={15} className="text-[#1D4ED8]" /> Live session room</span>
            <button onClick={() => setShowRoom(false)} className="p-1.5 rounded-lg" style={muted} data-testid="close-native-room"><X size={16} /></button>
          </div>
          <iframe
            title="Session room"
            src={jitsiUrl}
            allow="camera; microphone; fullscreen; display-capture; autoplay"
            className="w-full rounded-xl border-0"
            style={{ height: '70vh', minHeight: 480 }}
            data-testid="native-room-iframe"
          />
        </div>
      )}

      {/* Recap */}
      {isCompleted && (
        hasRecap ? (
          <div className="space-y-6" data-testid="session-recap">
            {session.ai_summary && (
              <div className="p-card rounded-2xl p-6">
                <h2 className="font-semibold mb-3 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}><FileText size={17} className="text-[#1D4ED8]" /> Session recap</h2>
                <p className="text-sm leading-relaxed whitespace-pre-wrap" style={muted} data-testid="recap-summary">{session.ai_summary}</p>
              </div>
            )}

            {takeaways.length > 0 && (
              <div className="p-card rounded-2xl p-6">
                <h2 className="font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}><CheckCircle2 size={17} className="text-emerald-500" /> Key takeaways</h2>
                <ul className="space-y-2.5" data-testid="recap-takeaways">
                  {takeaways.map((t, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm" style={muted}><CheckCircle2 size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" /> {t}</li>
                  ))}
                </ul>
              </div>
            )}

            {actionPlan.length > 0 && (
              <div className="p-card rounded-2xl p-6">
                <h2 className="font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}><ListChecks size={17} className="text-[#F97316]" /> Your action plan</h2>
                <div className="space-y-3" data-testid="recap-action-plan">
                  {actionPlan.map((a, i) => (
                    <div key={i} className="rounded-xl p-4 border" style={{ borderColor: 'var(--p-border)', background: 'var(--p-hover)' }}>
                      <div className="flex items-start gap-2.5">
                        <span className="w-6 h-6 rounded-full bg-[#F97316] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                        <div>
                          <div className="font-medium text-sm" style={{ color: 'var(--p-text)' }}>{a.task || a}</div>
                          {a.why && <div className="text-xs mt-1" style={muted}>{a.why}</div>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {goals.length > 0 && (
              <div className="p-card rounded-2xl p-6">
                <h2 className="font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}><Target size={17} className="text-[#1D4ED8]" /> Goals you're working toward</h2>
                <ul className="space-y-2.5" data-testid="recap-goals">
                  {goals.map((g, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm" style={muted}><Target size={15} className="text-[#1D4ED8] mt-0.5 flex-shrink-0" /> {g}</li>
                  ))}
                </ul>
              </div>
            )}

            {session.notes && (
              <div className="p-card rounded-2xl p-6">
                <h2 className="font-semibold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Coach notes</h2>
                <p className="text-sm leading-relaxed whitespace-pre-wrap" style={muted}>{session.notes}</p>
              </div>
            )}

            {session.transcript && (
              <div className="p-card rounded-2xl p-6">
                <button onClick={() => setShowTranscript((v) => !v)} className="w-full flex items-center justify-between" data-testid="toggle-transcript">
                  <span className="font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}><FileText size={17} style={muted} /> Full transcript</span>
                  <ChevronDown size={18} className={`transition-transform ${showTranscript ? 'rotate-180' : ''}`} style={muted} />
                </button>
                {showTranscript && (
                  <p className="text-sm leading-relaxed whitespace-pre-wrap mt-4 pt-4 border-t" style={{ ...muted, borderColor: 'var(--p-border)' }} data-testid="transcript-text">{session.transcript}</p>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="p-card rounded-2xl p-10 text-center" data-testid="recap-pending">
            <FileText size={36} className="mx-auto mb-3" style={{ color: 'var(--p-faint)' }} />
            <p className="text-sm" style={muted}>Your session recap and action plan will appear here once your coach finalizes the notes.</p>
          </div>
        )
      )}

      {/* Manual notes for upcoming sessions */}
      {!isCompleted && session.notes && (
        <div className="p-card rounded-2xl p-6" data-testid="session-agenda">
          <h2 className="font-semibold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Agenda / notes</h2>
          <p className="text-sm leading-relaxed whitespace-pre-wrap" style={muted}>{session.notes}</p>
        </div>
      )}
    </PortalLayout>
  );
}
